import { classNames } from 'tradera/utils/class-names';
import styles from './profile-header.module.scss';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import { RatingStars } from 'tradera/components/rating-stars/rating-stars';
import ALink from 'tradera/components/alink/alink';

import { useUrlLocalizer } from 'tradera/lang/translate';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { closeProfileMenu } from 'tradera/state/ui/reducer';
import { Button } from '../atoms/button/button';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { ReferralAlert } from './referral-alert';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { InternationalIdentificationAlert } from 'tradera/components/profile-menu/international-identification-alert';
import { Heading } from 'tradera/components/atoms/heading/heading';

const getGaLinkAttributes = (label: string) => ({
    'data-ga-category': 'Top navigation',
    'data-ga-action': 'Account menu',
    'data-ga-label': `Link - ${label}`,
});

type Props = {
    onClose?: () => void;
};

export const ProfileHeader = ({ onClose }: Props) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const dispatch = useAppDispatch();
    const handleCloseProfileMenu = () => dispatch(closeProfileMenu());
    const {
        memberAlias,
        cleanedMemberAlias,
        memberDetailedSellerRatingAverage: rating,
        memberId,
        memberCountryCodeIso2,
        memberIsBuyer,
        memberIsSeller,
    } = useAppSelector((state) => state.member);
    const showReferralAlert =
        useIsFeatureEnabled('member-get-member') &&
        (memberIsBuyer || memberIsSeller) &&
        isSwedenCountryCodeOrUndefined(memberCountryCodeIso2);

    return (
        <div className="d-flex flex-column gap-2 p-2 border-bottom position-relative">
            <div className="d-flex flex-column gap-1">
                <Heading
                    as="h2"
                    className={classNames('text-styled', styles.heading)}>
                    <ALink
                        href={toLocalizedUrl(
                            `/profile/items/${memberId}/${cleanedMemberAlias}`,
                        )}
                        useLink={true}
                        onClick={handleCloseProfileMenu}
                        className="text-truncate focus-visible"
                        {...getGaLinkAttributes('My profile from header')}>
                        {memberAlias}
                    </ALink>
                </Heading>
                {!!rating && cleanedMemberAlias && (
                    <div className="position-relative">
                        <RatingStars
                            memberAlias={cleanedMemberAlias}
                            memberId={memberId}
                            dark
                            rating={rating}
                            gaAttributes={getGaLinkAttributes(
                                'My profile from header',
                            )}
                            onClick={closeProfileMenu}
                            link
                        />
                    </div>
                )}
            </div>
            {showReferralAlert && <ReferralAlert />}
            <InternationalIdentificationAlert />
            {onClose && (
                <Button
                    theme="text"
                    className={styles.closeButton}
                    size="sm"
                    round
                    onClick={onClose}
                    icon={<CloseIcon className="icon icon-sm" />}
                />
            )}
        </div>
    );
};
